import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Schema component for adding JSON-LD structured data
 * Usage: <Schema data={schemaObject} />
 */
const Schema = ({ data }) => {
  if (!data) return null;

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(data)}
      </script>
    </Helmet>
  );
};

/**
 * Generate Breadcrumb schema
 */
export const createBreadcrumbSchema = (breadcrumbs, baseUrl = 'https://www.ascarii.com') => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": crumb.url ? `${baseUrl}${crumb.url}` : undefined
    }))
  };
};

/**
 * Generate Product schema
 */
export const createProductSchema = ({
  name,
  description,
  brand,
  image,
  aggregateRating,
  offers
}, baseUrl = 'https://www.ascarii.com') => {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": name,
    "description": description,
    "brand": {
      "@type": "Brand",
      "name": brand || "SAP Business One"
    },
    "image": image ? `${baseUrl}${image}` : undefined,
    "aggregateRating": aggregateRating ? {
      "@type": "AggregateRating",
      "ratingValue": aggregateRating.rating || "4.9",
      "reviewCount": aggregateRating.reviewCount || "150"
    } : undefined,
    "offers": {
      "@type": "Offer",
      "availability": offers?.availability || "https://schema.org/InStock",
      "priceCurrency": offers?.currency || "GBP",
      "price": offers?.price || undefined,
      "priceValidUntil": offers?.priceValidUntil || undefined,
      "seller": {
        "@type": "Organization",
        "name": "Ascarii"
      }
    }
  };
};

/**
 * Generate FAQ schema
 */
export const createFAQSchema = (faqs) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

/**
 * Generate Article schema for blog posts
 */
export const createArticleSchema = ({
  title,
  description,
  author,
  publishedDate,
  modifiedDate,
  image,
  category,
  url,
  wordCount
}, baseUrl = 'https://www.ascarii.com') => {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "description": description,
    "image": image || `${baseUrl}/ascarii-logo.png`,
    "author": {
      "@type": "Person",
      "name": author || "Ascarii Team"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Ascarii",
      "logo": {
        "@type": "ImageObject",
        "url": `${baseUrl}/ascarii-logo.png`,
        "width": 250,
        "height": 60
      },
      "url": baseUrl
    },
    "datePublished": publishedDate,
    "dateModified": modifiedDate || publishedDate,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url || baseUrl
    },
    "articleSection": category || "Blog",
    "wordCount": wordCount || undefined,
    "keywords": category,
    "inLanguage": "en-GB",
    "isAccessibleForFree": true
  };
};

export default Schema;
